<template>
    <cms-component v-bind="$props" :style="containerStyle" :class="{'fill-width': component.mediaProxy.fillWidth}">
        <cms-link :link="currentLink" :bound-item="boundItem" class="btn cms-button" :style="buttonStyle"
                  :class="buttonClasses">
            <template #before v-if="component.mediaProxy.leftIcon">
                <i class="fa-fw" :class="[fontAwesomePrefix, component.mediaProxy.leftIcon, {'mr-1': !component.mediaProxy.iconOnly}]"/>
            </template>
            <template v-if="!component.mediaProxy.iconOnly">
                <span v-if="suggestedText !== null" class="text-suggestion" v-text="suggestedText"
                      :title="tr(`Suggestion, use Apply all to apply it|Suggestion, utilisez Tout appliquer pour l'appliquer`)"/>
                <span v-else-if="isEditMode" ref="textEditor" class="text-editor" contenteditable :class="{'empty-placeholder': isEmpty}" v-text="text"
                      @input="edited" @blur="onBlur" @click="focusText"/>
                <span v-else class="text-renderer" v-text="text"/>
            </template>
            <template #after v-if="component.mediaProxy.rightIcon">
                <i class="fa-fw ml-1" :class="[fontAwesomePrefix, component.mediaProxy.rightIcon]"/>
            </template>
        </cms-link>
    </cms-component>
</template>

<script>
import CmsComponent from "@/components/editor/rendering/components/cms-component.vue";

// cms-component-button #hot-reload-debug
export default {
    name: `cms-component-button`,
    extends: CmsComponent,
    components: {CmsComponent},
    props: {
        placeholder: {type: String, default: `Button|Bouton`}
    },
    data() {
        return {
            text: ``,
            debounceTimer: null
        }
    },
    computed: {
        buttonClasses() {
            let list = [];
            list.push(`btn-${this.component.mediaProxy.size}`);
            list.push(`btn-type-${this.component.mediaProxy.buttonType}`);
            if (this.component.mediaProxy.buttonType === `custom`) {
                list.push(`btn-${this.component.buttonVariant || `primary`}`);
            } else {
                list.push(`btn-scheme`);
            }
            if (this.component.mediaProxy.iconOnly) {
                list.push(`icon-only`);
            }
            return list;
        },
        currentLink() {
            let link;
            if (this.useBinding && this.component.linkField) {
                link = this.boundItem.getField(this.component.linkField);
            }
            if (!link) {
                link = this.component.link;
            }
            return link;
        },
        containerStyle() {
            return {
                fontWeight: this.component.bold ? `bold` : ``,
                fontStyle: this.component.italic ? `italic` : `normal`
            }
        },
        buttonStyle() {
            let style = {}
            if (this.component.mediaProxy.borderRadius && this.component.mediaProxy.borderRadius !== `var(--border-radius)`) {
                style[`--border-radius`] = this.component.mediaProxy.borderRadius;
            }
            if (this.isEditMode) {
                style[`--text-empty-placeholder`] = `"[${this.$tr(this.placeholder)}]"`;
            }
            return style;
        },
        isEmpty() {
            return !this.text || this.text === `<br>`;
        },
        storedText() {
            return this.isEditMode ? this.component.getText(this.displayLanguage) : this.$tr(this.component.text);
        },
        suggestedText() {
            if (!this.$store.state.comparison.enabled)
                return null;
            const textDiff = (this.$store.state.comparison.suggestions || []).find(suggestion => suggestion.id === this.component.id);
            if (!textDiff || textDiff.status !== `different`)
                return null;
            return new DOMParser().parseFromString(textDiff.suggestion || ``, `text/html`).body.textContent.trim();
        }
    },
    mounted() {
        this.init();
    },
    created() {
        this.init();
    },
    methods: {
        init() {
            if (this.isEditMode) {
                this.text = this.component.getText(this.displayLanguage);
            } else {
                this.text = this.$tr(this.component.text);
            }
        },
        edited() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(this.updateText, 300);
        },
        updateText() {
            clearTimeout(this.debounceTimer);

            if (this.$refs.textEditor) {
                if (typeof this.component.newText !== `object`) {
                    this.component.newText = {};
                }
                this.component.newText[this.displayLanguage] = this.$refs.textEditor.innerHTML;
            }
        },
        onBlur() {
            this.updateText();
            // console.log(`blurred`, this.component.newText[this.displayLanguage]);
            this.component.applyChanges();
            this.init();
        },
        focusText() {
            if (this.$refs.textEditor && this.isEmpty) {
                const element = this.$refs.textEditor;
                const range = document.createRange();
                range.selectNodeContents(element);

                let selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    },
    watch: {
        block() {
            this.init();
        },
        displayLanguage() {
            this.init();
        },
        storedText() {
            if (!this.$refs.textEditor || document.activeElement !== this.$refs.textEditor) {
                this.init();
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.cms-component-button {
    &.fill-width {
        flex: 1;
    }

    .cms-button {
        display: flex;
        align-items: center;
        justify-content: var(--container-h-align);

        &.icon-only {
            aspect-ratio: 1;
            padding: 0.5em;
        }

        .text-editor {
            cursor: text;
            min-width: 20px;
            min-height: 20px;
            //flex: 1;

            &:not(:focus).empty-placeholder {
                &:before {
                    content: var(--text-empty-placeholder);
                    opacity: 0.65;
                }
            }

            &:focus {
                outline: var(--border-width) solid var(--color-editor-focus) !important;
                outline-offset: var(--border-width);
            }
        }

        .text-renderer {
            //flex: 1;
        }

        .text-suggestion {
            outline: 2px solid var(--color-review);
            outline-offset: 2px;
            border-radius: 2px;
        }
    }
}
</style>
